import React from 'react';
import { PhoneOutgoing, UserCheck } from 'lucide-react';
import { startOfDay, endOfDay } from 'date-fns';
import { MetricCard, type MetricCardTheme } from 'common/components';
import { useGetManagerCenterData } from 'services/hooks/queries';
import {
    capacityUnavailableReason,
    formatContactRate,
    formatHandleTime,
    formatHandleTimeBasis,
    formatOperatingWindow,
    formatSampleBasis,
} from './capacityProjection.utils';

import styles from './CapacityProjectionCard.module.scss';

const PROJECTED_CALLS_THEME: MetricCardTheme = {
    icon: PhoneOutgoing,
    color: 'var(--slai-ui-primary-purple-dark)',
    softBackground: 'var(--slai-ui-primary-purple-light)',
};

const PROJECTED_CONTACTS_THEME: MetricCardTheme = {
    icon: UserCheck,
    color: 'var(--slai-ui-info-color)',
    softBackground: 'color-mix(in srgb, var(--slai-ui-info-color) 18%, transparent)',
};

export type CapacityProjectionCardProps = {
    /** Both the dashboard endpoint and this card are manager-only; an agent session 403s. */
    isManager: boolean;
};

/**
 * Projected daily calls (FR-3.2). The assumptions are shown alongside the figure because the
 * projection divides by a rolling average handle time: without them the number looks like a
 * measurement rather than a forecast.
 */
export const CapacityProjectionCard: React.FC<CapacityProjectionCardProps> = ({ isManager }) => {
    const today = new Date();
    const { managerCenterData } = useGetManagerCenterData(
        { startDate: startOfDay(today), endDate: endOfDay(today) },
        isManager
    );

    const projection = managerCenterData?.capacity_projection;
    const inputs = projection?.inputs;

    const assumptions = inputs ? (
        <dl className={styles.assumptions} data-testid='capacity-projection-assumptions'>
            <div className={styles.assumption}>
                <dt>Active agents</dt>
                <dd>{inputs.active_agents.toLocaleString()}</dd>
            </div>
            <div className={styles.assumption}>
                <dt>Calls per agent</dt>
                <dd>{inputs.calls_to_agent_ratio.toLocaleString()}</dd>
            </div>
            <div className={styles.assumption}>
                <dt>Operating window</dt>
                <dd>{formatOperatingWindow(inputs)}</dd>
            </div>
            <div className={styles.assumption}>
                <dt>Avg handle time</dt>
                <dd>{formatHandleTime(inputs.avg_handle_time_minutes)}</dd>
                <dd className={styles.assumptionNote}>{formatHandleTimeBasis(inputs.avg_handle_time_basis)}</dd>
            </div>
            <div className={styles.assumption}>
                <dt>Contact rate</dt>
                <dd>{formatContactRate(inputs.contact_rate_pct)}</dd>
            </div>
            <div className={styles.assumption}>
                <dt>Based on</dt>
                <dd>{formatSampleBasis(inputs)}</dd>
            </div>
        </dl>
    ) : null;

    return (
        <section
            className='flex flex-col gap-lg'
            aria-labelledby='capacity-projection-heading'
            data-testid='capacity-projection-card'
        >
            <div className='flex flex-col gap-xs'>
                <h3 id='capacity-projection-heading'>Projected daily calls</h3>
                <p className='text-sm text-secondary'>
                    Today&apos;s tenant capacity, projected from active agents and a rolling average of recent call
                    data.
                </p>
            </div>

            {projection?.available ? (
                <>
                    <div className={styles.grid}>
                        <MetricCard
                            id='projected-daily-calls'
                            label='Projected daily calls'
                            tooltip='Active agents x calls per agent x operating minutes, divided by average handle time.'
                            theme={PROJECTED_CALLS_THEME}
                            count={projection.projected_daily_calls}
                            subtitle={inputs ? `${inputs.active_agents} active agents online` : undefined}
                            showPercent={false}
                        />
                        <MetricCard
                            id='projected-contacts'
                            label='Projected contacts'
                            tooltip='Projected daily calls at the tenant contact rate over the same window.'
                            theme={PROJECTED_CONTACTS_THEME}
                            count={projection.projected_contacts}
                            subtitle={
                                inputs ? `at a ${formatContactRate(inputs.contact_rate_pct)} contact rate` : undefined
                            }
                            showPercent={false}
                        />
                    </div>
                    {assumptions}
                </>
            ) : (
                <div className={styles.unavailable} data-testid='capacity-projection-unavailable'>
                    <p className={styles.unavailableCopy}>{capacityUnavailableReason(projection)}</p>
                    {assumptions}
                </div>
            )}
        </section>
    );
};

export default CapacityProjectionCard;
